import { StatusCardConfig, QueueTabConfig, SessionStatusCardConfig } from './types';

/** After this many overbooks, participant status becomes max_overbooks_reached (shown in UI as Overbooked) and they cannot be booked or assigned. */
export const MAX_PARTICIPANT_OVERBOOKS = 3;

export const STATUS_CARD_CONFIG: StatusCardConfig[] = [
  { key: 'endorsed', label: 'Endorsed', color: 'purple' },
  { key: 'contacting', label: 'Contacting', color: 'yellow' },
  { key: 'scheduled', label: 'Scheduled', color: 'blue' },
  { key: 'unreachable', label: 'Unreachable', color: 'gray' },
  { key: 'dropout', label: 'Dropout', color: 'red' },
  { key: 'reschedule', label: 'Pending Reschedule', color: 'yellow' },
  { key: 'called_off', label: 'Called off', color: 'orange' },
] as const;

export const SESSION_STATUS_CARD_CONFIG: SessionStatusCardConfig[] = [
  {
    key: 'scheduled',
    label: 'Scheduled',
    color: 'blue',
    tooltip: 'Sessions that are booked but have not yet started',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    color: 'purple',
    tooltip: 'Sessions that are currently active and ongoing',
  },
  {
    key: 'completed',
    label: 'Completed',
    color: 'green',
    tooltip: 'Sessions that have been successfully finished',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    color: 'gray',
    tooltip: 'Sessions that were cancelled by the team',
  },
  {
    key: 'called_off',
    label: 'Called Off',
    color: 'orange',
    tooltip: 'Sessions that were called off by the participant',
  },
  {
    key: 'no_show',
    label: 'No Show',
    color: 'rose',
    tooltip: 'Sessions where the participant did not attend',
  },
  {
    key: 'reschedule',
    label: 'Pending Reschedule',
    color: 'yellow',
    tooltip: 'Sessions requested to be moved to a different time',
  },
  {
    key: 'rescheduled',
    label: 'Rescheduled',
    color: 'blue',
    tooltip: 'Sessions that were moved to a different time',
  },
  {
    key: 'partially_completed',
    label: 'Partially Completed',
    color: 'cyan',
    tooltip: 'Sessions that were started but not fully completed',
  },
  {
    key: 'disqualified',
    label: 'Disqualified',
    color: 'red',
    tooltip: 'Participants who no longer meet the study criteria',
  },
  {
    key: 'blocked',
    label: 'Do Not Hire',
    color: 'stone',
    tooltip: 'Participants marked as not to be booked again',
  },
  {
    key: 'overbooked',
    label: 'Overbooked',
    color: 'yellow',
    tooltip: 'Participants who reached the maximum number of bookings',
  },
];

/** Top summary strip only — omits statuses that are tracked but not shown as KPI tiles. */
export const SESSION_STATUS_SUMMARY_CARD_CONFIG: SessionStatusCardConfig[] =
  SESSION_STATUS_CARD_CONFIG.filter(
    (c) =>
      c.key !== 'in_progress' &&
      c.key !== 'partially_completed' &&
      c.key !== 'reschedule',
  );

/** Keys + labels for Session Status advanced filter & deep links (full list). */
export type SchedulerSessionStatusFilterOption = {key: string; label: string};

export const SCHEDULER_SESSION_STATUS_FILTER_OPTIONS: SchedulerSessionStatusFilterOption[] =
  [
    {key: 'to_be_scheduled', label: 'To Be Scheduled'},
    {key: 'invited', label: 'Invited'},
    ...SESSION_STATUS_CARD_CONFIG.map((c) => ({key: c.key, label: c.label})),
  ];

export const QUEUE_TABS: QueueTabConfig[] = [
  { value: 'all', label: 'All Queue' },
  { value: 'my', label: 'My Queue' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'group_booking', label: 'Group Sessions' },
];

/** Optional many-to-one grouping for participant `schedule_status` filter labels (Participant Status column / advanced filter). */
export type SchedulerParticipantStatusFilterGroup = {
  label: string;
  values: readonly string[];
};

/**
 * Empty by default: every raw schedule_status now gets its own distinct
 * filter label (via formatStatusDisplay, the same function the grid badge
 * uses), instead of bucketing e.g. 'unreachable'/'dropout'/'called_off' into
 * one shared "Not reachable" label a user could never find "Called Off"
 * under. Kept as a parameter (rather than removed outright) in case a
 * genuine many-to-one grouping is intentionally wanted again later.
 */
export const SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS: SchedulerParticipantStatusFilterGroup[] =
  [];

export const STATUS_OPTIONS = [
  'endorsed',
  'contacting',
  'scheduled',
  'unreachable',
  'available',
  'dropout',
  'reschedule',
  'called_off',
  'disqualified',
  'blocked',
  'overbooked',
  'max_overbooks_reached',
] as const;
